import { Suspense, useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import type { walletCounterparties_Query } from "@app/queries/walletCounterparties_Query.graphql";
import WalletCounterpartyPanel, { type CellScope } from "./wallet-counterparty-panel";

const counterpartiesQuery = graphql`
  query walletCounterparties_Query($slug: String!, $year: Int) {
    walletCounterparties(slug: $slug, year: $year) {
      counterparty
      yearTotal
      points {
        month
        value
      }
    }
  }
`;

const CURRENT_YEAR = new Date().getFullYear();

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const money = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

const monthLabel = (yyyymm: string) =>
  MONTH_LABELS[Number(yyyymm.slice(5, 7)) - 1] ?? yyyymm;

// Per-counterparty view of who was paid through this wallet, month by month for one
// year — the payment twin of /revenue, read straight from the staged (un-promoted)
// upload CSVs. Every positive cell drills into the imported rows behind it.
export default function WalletCounterparties({
  slug,
  name,
}: {
  slug: string;
  name: string;
}) {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [cell, setCell] = useState<CellScope | null>(null);

  return (
    <div className="bg-white rounded border p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-semibold">Counterparties paid through {name}</h2>
          <p className="text-xs text-gray-400">
            Who was paid, month by month, straight from the imported {name} CSVs
            (staged — independent of the ledger). Click any amount to see the
            transactions behind it.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1 text-sm">
          <button
            type="button"
            onClick={() => setYear((y) => y - 1)}
            className="px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50"
            title="Previous year"
          >
            ‹
          </button>
          <span className="px-2 tabular-nums font-medium text-gray-800">{year}</span>
          <button
            type="button"
            disabled={year >= CURRENT_YEAR}
            onClick={() => setYear((y) => y + 1)}
            className="px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            title="Next year"
          >
            ›
          </button>
        </div>
      </div>

      <Suspense fallback={<GridSkeleton />}>
        <Grid slug={slug} year={year} onCell={setCell} />
      </Suspense>

      {cell && (
        <WalletCounterpartyPanel
          slug={slug}
          year={year}
          scope={cell}
          onClose={() => setCell(null)}
        />
      )}
    </div>
  );
}

function Grid({
  slug,
  year,
  onCell,
}: {
  slug: string;
  year: number;
  onCell: (scope: CellScope) => void;
}) {
  const data = useLazyLoadQuery<walletCounterparties_Query>(
    counterpartiesQuery,
    { slug, year },
    { fetchPolicy: "store-or-network" }
  );
  const rows = data.walletCounterparties;

  if (rows.length === 0) {
    return (
      <div className="border border-dashed border-gray-200 rounded-lg p-8 text-center">
        <p className="text-sm text-gray-500">No {slug} payments staged for {year}.</p>
      </div>
    );
  }

  const months = rows[0].points.map((point) => point.month);
  const monthTotal = (month: string) =>
    rows.reduce(
      (sum, row) => sum + (row.points.find((p) => p.month === month)?.value ?? 0),
      0
    );
  const grandTotal = rows.reduce((sum, row) => sum + row.yearTotal, 0);

  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg">
      <table className="min-w-[1100px] w-full text-sm border-collapse">
        <thead>
          <tr className="text-xs text-gray-400 uppercase border-b border-gray-200">
            <th className="sticky left-0 z-20 bg-white text-left font-medium px-3 py-2 min-w-[14rem]">
              Counterparty
            </th>
            {months.map((month) => (
              <th key={month} className="px-3 py-2 text-right font-medium tabular-nums whitespace-nowrap">
                {monthLabel(month)}
              </th>
            ))}
            <th className="px-3 py-2 text-right font-semibold tabular-nums whitespace-nowrap text-gray-500 border-l border-gray-100">
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.counterparty} className="border-b border-gray-100 hover:bg-gray-50/60">
              <td className="sticky left-0 z-10 bg-white px-3 py-1.5 min-w-[14rem]">
                <span className="font-medium text-gray-800 truncate">{row.counterparty}</span>
              </td>
              {row.points.map((point) => (
                <Cell
                  key={point.month}
                  value={point.value ?? 0}
                  onClick={() =>
                    onCell({
                      kind: "cp-month",
                      counterparty: row.counterparty,
                      label: `${row.counterparty} · ${monthLabel(point.month)} ${year}`,
                      month: point.month,
                    })
                  }
                />
              ))}
              <Cell
                value={row.yearTotal}
                strong
                onClick={() =>
                  onCell({
                    kind: "cp-year",
                    counterparty: row.counterparty,
                    label: `${row.counterparty} · ${year}`,
                  })
                }
              />
            </tr>
          ))}

          <tr className="border-t-2 border-gray-200 bg-gray-50 font-semibold text-gray-800">
            <td className="sticky left-0 z-10 bg-gray-50 px-3 py-1.5 min-w-[14rem]">
              All counterparties
            </td>
            {months.map((month) => (
              <Cell
                key={month}
                value={monthTotal(month)}
                strong
                onClick={() =>
                  onCell({ kind: "all-month", label: `All ${slug} · ${monthLabel(month)} ${year}`, month })
                }
              />
            ))}
            <Cell
              value={grandTotal}
              strong
              onClick={() => onCell({ kind: "all-year", label: `All ${slug} · ${year}` })}
            />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function Cell({
  value,
  onClick,
  strong,
}: {
  value: number;
  onClick: () => void;
  strong?: boolean;
}) {
  return (
    <td className={`px-3 py-1.5 text-right tabular-nums whitespace-nowrap ${strong ? "font-semibold border-l border-gray-100" : ""}`}>
      {value <= 0 ? (
        <span className="text-gray-300">—</span>
      ) : (
        <button
          type="button"
          onClick={onClick}
          title="View the imported transactions behind this amount"
          className="hover:underline hover:text-gray-900 cursor-pointer"
        >
          {money(value)}
        </button>
      )}
    </td>
  );
}

function GridSkeleton() {
  return (
    <div className="border border-gray-200 rounded-lg p-3 animate-pulse">
      {Array.from({ length: 5 }).map((_, row) => (
        <div key={row} className="flex items-center gap-3 py-2">
          <div className="h-3 bg-gray-100 rounded w-48" />
          <div className="ml-auto flex gap-3">
            {Array.from({ length: 12 }).map((_, col) => (
              <div key={col} className="h-3 w-8 bg-gray-100 rounded" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
